import { useCallback, useMemo, useState } from "react";
import type { Task } from "@common/types/task";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type { TaskListCapabilities } from "../taskListCapabilities";

const NO_TASK_IDS: ReadonlySet<string> = new Set();

interface UseTaskBoardSelectionArgs<TTask extends Task> {
    tasks: readonly TTask[];
    getPlacements: (task: Task) => readonly BoardPlacement[];
    /** Whether the board shows a card at a placement (expanded column and lane). */
    isPlacementShown: (placement: BoardPlacement) => boolean;
    mode: TaskListCapabilities["boardSelection"];
}

/** The selection with only ids still on screen; the same set when none left. */
const keepShown = (
    taskIds: ReadonlySet<string>,
    shownTaskIds: ReadonlySet<string>,
) =>
    [...taskIds].every((taskId) => shownTaskIds.has(taskId))
        ? taskIds
        : new Set([...taskIds].filter((taskId) => shownTaskIds.has(taskId)));

/** Busy closes demo cards never join the selection, so no bulk action touches them. */
const withoutBusyClosesDemoIds = (taskIds: ReadonlySet<string>) =>
    [...taskIds].some(isBusyClosesDemoId)
        ? new Set([...taskIds].filter((taskId) => !isBusyClosesDemoId(taskId)))
        : taskIds;

/**
 * One card at a time: a ⌘-click, X or Shift+arrow that adds a card makes it
 * the selection; a gesture that adds several (⌘A, a Shift-click run) is
 * ignored.
 */
const keepOneCard = (
    previous: ReadonlySet<string>,
    next: ReadonlySet<string>,
): ReadonlySet<string> => {
    if (next.size <= 1) {
        return next;
    }
    const addedIds = [...next].filter((taskId) => !previous.has(taskId));
    return addedIds.length === 1 ? new Set(addedIds) : previous;
};

/**
 * The board's card selection. Tasks that leave the screen (filtered out,
 * moved into a collapsed column or lane) drop out of it, so the island only
 * ever acts on cards the user can see. In `single` mode it holds one card.
 */
export const useTaskBoardSelection = <TTask extends Task>({
    tasks,
    getPlacements,
    isPlacementShown,
    mode,
}: UseTaskBoardSelectionArgs<TTask>) => {
    const [selectedIds, setSelectedIds] =
        useState<ReadonlySet<string>>(NO_TASK_IDS);
    const shownTaskIds = useMemo(
        () =>
            new Set(
                tasks
                    .filter((task) =>
                        getPlacements(task).some(isPlacementShown),
                    )
                    .map((task) => task.id),
            ),
        [tasks, getPlacements, isPlacementShown],
    );
    const shownSelectedIds = keepShown(selectedIds, shownTaskIds);
    if (shownSelectedIds !== selectedIds) {
        setSelectedIds(shownSelectedIds);
    }

    const onSelectionChange = useCallback(
        (requested: ReadonlySet<string>) => {
            const next = withoutBusyClosesDemoIds(requested);
            setSelectedIds((previous) =>
                mode === "single" ? keepOneCard(previous, next) : next,
            );
        },
        [mode],
    );
    const clearSelection = useCallback(() => setSelectedIds(NO_TASK_IDS), []);
    const selectedTasks = useMemo(
        () => tasks.filter((task) => shownSelectedIds.has(task.id)),
        [tasks, shownSelectedIds],
    );

    return {
        selectedItemIds: shownSelectedIds,
        selectedTasks,
        onSelectionChange,
        clearSelection,
    };
};
